import { useSearchParams } from "react-router-dom";

/**
 * A paged list's page number and filters, kept in the address
 * (`?page=2&status=completed`) so Back from an item returns to the same page
 * and view. Values equal to their default, and page 1, are left out of the
 * address. Updates don't move the scroll position; the list scrolls itself
 * (see useScrollIntoViewOnChange / bringIntoView).
 */
export function useListParams<T extends Record<string, string>>(defaults: T, allowed: Partial<Record<keyof T, readonly string[]>> = {}) {
  const [params, setParams] = useSearchParams();
  const raw = Number(params.get("page") || 1);
  const page = Number.isSafeInteger(raw) && raw > 0 && raw <= 100000 ? raw : 1;
  const get = (key: keyof T & string): string => {
    const value = params.get(key);
    const options = allowed[key];
    if (value === null || (options && !options.includes(value))) return defaults[key];
    return value.slice(0, 200);
  };
  /** Changes several values in one step; pass page: 1 to start the list again. */
  const update = (changes: Partial<Record<(keyof T & string) | "page", string | number>>) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([key, value]) => {
      const text = String(value ?? "");
      if (!text || (key === "page" ? text === "1" : text === defaults[key])) next.delete(key);
      else next.set(key, text);
    });
    setParams(next, { preventScrollReset: true });
  };
  return { page, get, update };
}
